import { useEffect, useRef, useState } from 'react';
import type { FocusEvent, KeyboardEvent, MutableRefObject } from 'react';
import type { EditorScreen, EditorBlock } from '@/store/editorSlice';
import { blockAutoAdvances } from './answerCollection';

/**
 * WEB-1794: через сколько тишины показывать запасную кнопку «Далее» на экране,
 * где авто-переход упёрся в незавершённый ввод.
 *
 * ⚠ Это таймер ПОКАЗА КНОПКИ, а не таймер перехода. По его истечении не
 * происходит ничего, кроме появления кнопки; уводит с экрана по-прежнему
 * только действие человека (завершение ввода или клик по этой кнопке).
 * Отвергнутая в WEB-1643 «таймер-угадайка» была про другое — там пауза в
 * наборе САМА уносила человека вперёд с недописанным ответом.
 *
 * Отсчёт перезапускается на каждое изменение значений (см. эффект ниже):
 * пока человек набирает, кнопка не появляется и не дёргается. Появившись,
 * она уже не прячется — мигание хуже лишней кнопки.
 */
const AUTO_ADVANCE_RESCUE_DELAY_MS = 1200;

export interface UseAutoAdvanceParams {
  /** Экраны воронки в порядке показа. */
  screens: EditorScreen[];
  /** Индекс текущего экрана (уже приведённый к границам списка). */
  safeIndex: number;
  /** Значения формы: ключ — id блока. Их изменение и запускает попытку. */
  values: Record<string, string>;
  /** Квиз пройден — на шаге пейволла авто-переход не работает. */
  isCompleted: boolean;
  /**
   * ЖАЛОБА ПОКУПАТЕЛЯ 18.09.2026 — отметки `экран:блок`, по которым
   * авто-переход УЖЕ срабатывал в прошлой жизни вкладки (из снимка прогресса,
   * quizProgressStorage.ts). Без них перезагрузка обнуляла память узла, а
   * ответы покупателя — нет: «Назад» на экран с выбором варианта немедленно
   * уносило вперёд, и вернуться было невозможно.
   *
   * Значения ответа отметка не несёт: оно достраивается здесь из `values` —
   * тех самых восстановленных ответов. Поэтому смена ответа после
   * перезагрузки даёт другой ключ, и переход снова работает, как и должен.
   */
  restoredAutoAdvanced?: readonly string[];
  /**
   * Сообщение наружу о СОСТОЯВШЕМСЯ переходе — той же отметкой `экран:блок`,
   * чтобы вызывающий положил её в снимок прогресса. Не состоявшийся переход
   * не сообщается: отметка на нём заперла бы экран навсегда (WEB-1540).
   */
  onAutoAdvanced?: (blockKey: string) => void;
  /**
   * Решение владельца №4 (2026-10-08): пауза перед переходом, мс
   * (`states.advanceDelayMs` пака, autoAdvanceDelay.ts). 0/отсутствие —
   * переход в том же эффекте, ровно как было. Пока пауза идёт, новый выбор
   * перезапускает её с новым значением (переход один, по последнему выбору).
   */
  advanceDelayMs?: number;
}

export interface UseAutoAdvanceResult {
  /**
   * Сюда вызывающий КАЖДЫЙ рендер кладёт свой handleNext (он возвращает
   * признак «продвижение состоялось», WEB-1540). Именно ссылка, а не аргумент
   * хука: handleNext в визарде объявлен НИЖЕ по файлу — после раннего выхода
   * по проверке способа оплаты, — а хук обязан вызываться выше любого
   * раннего выхода (правила хуков). Пока ссылку не заполнили, попытка
   * возвращает false, то есть «переход не состоялся».
   */
  handleNextRef: MutableRefObject<() => boolean>;
  /**
   * WEB-1794: пора показать запасную кнопку «Далее» — попытка авто-перехода на
   * ЭТОМ экране была, не состоялась, и с тех пор прошло
   * AUTO_ADVANCE_RESCUE_DELAY_MS тишины. Это ровно «таймер истёк»; остальные
   * условия показа (авто-переход — единственный выход) вызывающий проверяет
   * сам, рядом с остальными слагаемыми кнопки.
   */
  rescueVisible: boolean;
  /** Вешается на корень экрана: onBlur — одна из двух дверей сигнала WEB-1643. */
  onScreenFocusOut: (e: FocusEvent<HTMLDivElement>) => void;
  /** Вешается на корень экрана: onKeyDown — вторая дверь того же сигнала. */
  onScreenKeyDown: (e: KeyboardEvent<HTMLDivElement>) => void;
}

/**
 * WEB-1796: узел авто-перехода, вынесенный из QuizScreenWizard без изменения
 * поведения. Внутри — эффект попытки перехода (WEB-447 P1-3 + WEB-1540),
 * счётчик завершений ввода и пара «ключ + счётчик» (WEB-1643) и отложенный
 * показ запасной кнопки (WEB-1794).
 */
export function useAutoAdvance({
  screens,
  safeIndex,
  values,
  isCompleted,
  restoredAutoAdvanced,
  onAutoAdvanced,
  advanceDelayMs = 0,
}: UseAutoAdvanceParams): UseAutoAdvanceResult {
  // WEB-447 P1-3: single_choice auto-advance. When the current screen holds a
  // single_choice with autoAdvance:true that has just received a value, advance
  // immediately (no manual Next) via the normal decision chain. Guarded by a
  // per-(screen,block,value) key so navigating Back to an already-answered
  // screen does not bounce the user forward again (traffback-scar class).
  // MUST live above the early-return in the caller to respect the Rules of
  // Hooks; it invokes handleNext through a ref assigned later in that render.
  //
  // ЖАЛОБА 18.09: набор засевается восстановленными отметками ОДИН раз, при
  // первом рендере. Отметка приходит без ответа (`экран:блок`), поэтому
  // значение достраивается из ответов, восстановленных тем же снимком. Нет
  // ответа (воронка с исключённым вводом, решение владельца 17.09) — отметка
  // просто не заводится: поведение остаётся прежним, а лишнего ключа,
  // запирающего экран, не появляется.
  const [seededAutoAdvanced] = useState<Set<string>>(() => {
    const seeded = new Set<string>();
    for (const entry of restoredAutoAdvanced ?? []) {
      const separator = entry.indexOf(':');
      if (separator <= 0) continue;
      const blockId = entry.slice(separator + 1);
      const value = values[blockId];
      if (!value) continue;
      seeded.add(`${entry}:${value}`);
    }
    return seeded;
  });
  const autoAdvancedRef = useRef<Set<string>>(seededAutoAdvanced);
  const handleNextRef = useRef<() => boolean>(() => false);
  // Ссылкой, а не прямым вызовом: эффект ниже намеренно не зависит от
  // вызывающего (его функция пересоздаётся каждый рендер), и лишняя
  // зависимость перезапускала бы попытку перехода.
  const onAutoAdvancedRef = useRef(onAutoAdvanced);
  onAutoAdvancedRef.current = onAutoAdvanced;
  // WEB-1643: счётчик «ввод на экране завершён» — растёт на потере фокуса
  // полем ввода и на Enter (onScreenFocusOut/onScreenKeyDown ниже,
  // подвешены к корню экрана). Только его рост даёт
  // авто-переходу ПОВТОРНУЮ попытку; сам по себе набор текста не даёт.
  const [inputCommitTick, setInputCommitTick] = useState(0);
  // Последняя сделанная попытка авто-перехода: по какому ключу и при каком
  // значении счётчика завершений. Пара (ключ, счётчик) отличает «человек
  // только что выбрал вариант» от «человек добивает соседнее поле».
  const autoAdvanceAttemptRef = useRef<{ key: string; tick: number } | null>(
    null,
  );
  // WEB-1794: экран, на котором попытка авто-перехода УЖЕ была сделана и НЕ
  // состоялась (человек остался тут по снимаемой им причине — незаполненное
  // обязательное поле, недописанная почта). Именно этот признак включает
  // запасную кнопку «Далее»: без него на авто-переходном экране кнопки нет по
  // построению, и замерший с фокусом в поле человек упирается в тупик.
  const [autoAdvanceStalledScreenId, setAutoAdvanceStalledScreenId] = useState<
    string | null
  >(null);
  useEffect(() => {
    if (isCompleted) return;
    const cur = screens[safeIndex];
    if (!cur) return;
    const block = cur.blocks.find(
      (b: EditorBlock) =>
        // WEB-682b: loader_processing пишет values[id]='done' по завершении
        // шагов (LoaderProcessingBlock) — тот же контракт, что клик
        // single_choice: значение появилось → авто-Next, key-guard ниже
        // защищает от повторного прыжка при возврате Назад.
        blockAutoAdvances(b) && !!values[b.id],
    );
    if (!block) return;
    const key = `${cur.id}:${block.id}:${values[block.id]}`;
    if (autoAdvancedRef.current.has(key)) return;
    // WEB-1540: отметка «попытка авто-перехода использована» ставится ТОЛЬКО
    // по факту СОСТОЯВШЕГОСЯ перехода (способ А карточки). Раньше она стояла
    // ДО вызова, и экран, где блок с авто-переходом соседствует с обязательным
    // (WEB-895), становился тупиком: клик по варианту упирался в гейт
    // обязательных полей, отметка при этом уже была записана, а второй попытки
    // не будет никогда — и запасной кнопки «Далее» на таком экране тогда не
    // было по построению (screenAdvanceIsAutoOnly гасил ctaNode). Теперь
    // заполнение соседнего обязательного поля меняет values, эффект
    // перезапускается, отметки нет — переход состоится без повторного клика по
    // варианту. Защита от зацикливания не ослабла: после состоявшегося
    // перехода отметка стоит, и возврат Назад (values те же) вперёд не
    // выталкивает.
    //
    // ⚠ Две фразы выше с тех пор перестали быть правдой целиком — дописано
    // ревизией пачки 8, чтобы читающий не строил план на устаревшем:
    //  - «переход состоится САМ» — только после WEB-1643 (ниже): заполнения
    //    поля мало, нужен признак ЗАВЕРШЁННОГО ввода (потеря фокуса полем либо
    //    Enter). На выборе варианта, как и раньше, всё происходит сразу;
    //  - «запасной кнопки нет по построению» — было верно до WEB-1794: теперь
    //    на застрявшем авто-переходном экране кнопка появляется через
    //    AUTO_ADVANCE_RESCUE_DELAY_MS тишины (ветка rescue в ctaNode визарда).
    //    Тупик, описанный здесь, ею и закрыт — это второй выход, а не замена
    //    правилу WEB-1540.
    //
    // WEB-1643: у этой поправки была цена. Текстовое поле пишет значение в
    // FormValues на КАЖДОЕ нажатие клавиши, а «заполнено» для гейта WEB-895 =
    // непустая строка — значит эффект перезапускался на первом же символе и
    // уносил человека вперёд с ответом «И» вместо «Иван» (порча данных лида).
    // Теперь повторная попытка требует признака ЗАВЕРШЁННОГО ввода:
    //  - ключ изменился (человек только что выбрал/сменил вариант ответа —
    //    это само по себе явное действие) → пробуем сразу, как раньше;
    //  - ключ тот же, попытка по нему уже была → пробуем ТОЛЬКО когда вырос
    //    счётчик завершений ввода (потеря фокуса полем либо Enter).
    // Таймер-угадайку («пауза в наборе») намеренно не берём: пауза в наборе
    // не значит, что человек дописал, — «И» с задумчивой паузой уехало бы тем
    // же порченым лидом, ради которого всё и затевалось.
    const attempt = autoAdvanceAttemptRef.current;
    if (attempt && attempt.key === key && attempt.tick === inputCommitTick) {
      return;
    }
    const tryAdvance = (): void => {
      autoAdvanceAttemptRef.current = { key, tick: inputCommitTick };
      // WEB-1794: результат попытки — не только «запомнить, что переход
      // состоялся», но и «запомнить, что он НЕ состоялся». Второе включает
      // запасную кнопку (ветка rescue в ctaNode визарда). Семантику WEB-1643 это
      // не трогает: условие повторной попытки выше не изменилось.
      if (handleNextRef.current()) {
        autoAdvancedRef.current.add(key);
        // Жалоба 18.09: та же отметка уезжает наружу (в снимок прогресса) — но
        // БЕЗ значения ответа, только «экран:блок».
        onAutoAdvancedRef.current?.(`${cur.id}:${block.id}`);
        setAutoAdvanceStalledScreenId(null);
      } else {
        setAutoAdvanceStalledScreenId(cur.id);
      }
    };
    // Решение владельца №4 (2026-10-08): пауза перед переходом, чтобы
    // выбранный вид успел показаться. Отметка попытки пишется ВНУТРИ таймера:
    // перезапуск эффекта (новый выбор, смена экрана) снимает таймер, и
    // записанная заранее отметка не дала бы поставить новый — переход
    // потерялся бы. Пауза 0 — прежний синхронный путь, байт-в-байт.
    if (advanceDelayMs > 0) {
      const timer = setTimeout(tryAdvance, advanceDelayMs);
      return () => clearTimeout(timer);
    }
    tryAdvance();
    return undefined;
    // handleNext is invoked via ref (recreated each render); the key-set guard
    // makes any re-run idempotent, so it is intentionally not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, safeIndex, isCompleted, inputCommitTick, advanceDelayMs]);

  /**
   * WEB-1643: «человек закончил вводить» — ЕДИНСТВЕННАЯ дверь сигнала.
   *
   * Слушателя вешаем не на каждый блок ввода, а на корень экрана: focusout и
   * keydown всплывают, поэтому один обработчик покрывает все поля — и те, что
   * появятся позже. Разложить onBlur по каждому типу поля значило бы завести
   * список, который неизбежно разъедется (новый тип поля забудут — и на нём
   * снова уедет первый символ).
   */
  const isFormControl = (el: EventTarget | null): boolean => {
    const node = el as HTMLElement | null;
    if (!node || typeof node.tagName !== 'string') return false;
    return (
      node.tagName === 'INPUT' ||
      node.tagName === 'TEXTAREA' ||
      node.tagName === 'SELECT' ||
      node.isContentEditable === true
    );
  };

  const onScreenFocusOut = (e: FocusEvent<HTMLDivElement>) => {
    // Фокус ушёл не из поля ввода (кнопка, вариант ответа) — это не «ввод
    // завершён», сигналом не считаем.
    if (!isFormControl(e.target)) return;
    // Фокус переехал в ДРУГОЕ поле того же экрана — человек ещё заполняет
    // экран (таб из имени в почту). Уносить его сейчас значило бы потерять
    // второе поле.
    const next = e.relatedTarget as HTMLElement | null;
    if (next && isFormControl(next) && e.currentTarget.contains(next)) return;
    setInputCommitTick((t) => t + 1);
  };

  const onScreenKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return;
    if (!isFormControl(e.target)) return;
    setInputCommitTick((t) => t + 1);
  };

  /**
   * WEB-1794: отложенный ПОКАЗ запасной кнопки на застрявшем экране.
   *
   * Тайминг здесь ради одного: при штатном заполнении кнопка не должна
   * мелькать. Штатный путь — «набрал и ушёл из поля»: между последним символом
   * и потерей фокуса проходят доли секунды, и переход случается сам. Тупик —
   * «набрал и замер»: значения не меняются, ничего не происходит. Отличает их
   * только тишина, поэтому отсчёт перезапускается на каждое изменение values и
   * на каждый сигнал завершения ввода.
   *
   * Показанную кнопку обратно не прячем: исчезающая кнопка «Далее» пугает
   * сильнее, чем лишняя. Гасится она только сменой экрана или состоявшимся
   * переходом — оба случая снимают признак застревания.
   */
  const [autoAdvanceRescueVisible, setAutoAdvanceRescueVisible] =
    useState(false);
  const stalledHere =
    !!autoAdvanceStalledScreenId &&
    autoAdvanceStalledScreenId === screens[safeIndex]?.id;
  useEffect(() => {
    if (!stalledHere) {
      setAutoAdvanceRescueVisible(false);
      return undefined;
    }
    const timer = setTimeout(
      () => setAutoAdvanceRescueVisible(true),
      AUTO_ADVANCE_RESCUE_DELAY_MS,
    );
    return () => clearTimeout(timer);
    // values и inputCommitTick внутри не читаются — они здесь ради
    // ПЕРЕЗАПУСКА отсчёта: любое движение человека начинает тишину заново.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stalledHere, values, inputCommitTick]);

  return {
    handleNextRef,
    rescueVisible: autoAdvanceRescueVisible,
    onScreenFocusOut,
    onScreenKeyDown,
  };
}
